import type { ReactNode } from 'react';
import { type StackProps, type TextProps, XStack, YStack } from 'tamagui';

import { IconButton } from '../buttons/Button';
import { Icon, type IconType } from '../icons/Icon';
import { BodyMediumRegular, LabelRegular } from '../text/Typography';

export interface RowHeaderRightButton {
    icon: IconType;
    onPress: () => void;
    variant?: 'primary' | 'secondary' | 'tertiary';
    tone?: 'neutral' | 'positive' | 'danger';
    disabled?: boolean;
}

export interface RowHeaderProps extends StackProps {
    title: string;
    titleProps?: TextProps;
    subtitle?: string | null;
    subtitleProps?: TextProps;
    label?: string | null;
    labelProps?: TextProps;
    leftIcon?: IconType;
    leftContentOverride?: React.ReactElement;
    rightContent?: ReactNode;
    primaryButton?: RowHeaderRightButton;
    secondaryButton?: RowHeaderRightButton;
}

export const RowHeader = ({
    title,
    titleProps,
    subtitle,
    subtitleProps,
    label,
    labelProps,
    leftIcon,
    leftContentOverride,
    rightContent,
    primaryButton,
    secondaryButton,
    ...stackProps
}: RowHeaderProps) => {
    return (
        <XStack
            backgroundColor="$gray200"
            alignItems="center"
            justifyContent="space-between"
            gap={8}
            marginBottom={8}
            padding={16}
            {...stackProps}
        >
            <XStack flex={1} alignItems="center" gap={12}>
                {leftContentOverride != null ? (
                    <>{leftContentOverride}</>
                ) : leftIcon != null ? (
                    <Icon icon={leftIcon} size={18} color="$gray1200" />
                ) : null}
                <YStack flex={1} gap={4}>
                    <XStack alignItems="center" gap={12}>
                        <BodyMediumRegular {...titleProps}>
                            {title}
                        </BodyMediumRegular>
                        {label != null && (
                            <LabelRegular color="$gray1000" {...labelProps}>
                                {label}
                            </LabelRegular>
                        )}
                    </XStack>
                    {subtitle != null && (
                        <LabelRegular color="$gray1000" {...subtitleProps}>
                            {subtitle}
                        </LabelRegular>
                    )}
                </YStack>
            </XStack>
            <XStack alignItems="center" gap={8}>
                {rightContent != null && <>{rightContent}</>}
                {secondaryButton != null && (
                    <IconButton
                        icon={secondaryButton.icon}
                        onPress={secondaryButton.onPress}
                        variant={secondaryButton.variant ?? 'tertiary'}
                        tone={secondaryButton.tone}
                        disabled={secondaryButton.disabled}
                    />
                )}
                {primaryButton != null && (
                    <IconButton
                        icon={primaryButton.icon}
                        onPress={primaryButton.onPress}
                        variant={primaryButton.variant ?? 'tertiary'}
                        tone={primaryButton.tone}
                        disabled={primaryButton.disabled}
                    />
                )}
            </XStack>
        </XStack>
    );
};
